import { useState } from "react";
import { generateTeacherFeedback } from "@/projects/lib/ai.functions";
import { Sparkles, Loader2, Copy, Check } from "lucide-react";
import { Button } from "@/projects/components/ui/button";
import { toast } from "sonner";

type Props = {
  studentName: string;
  videoTitle: string;
  decision: "Ready for Reward" | "Not Proper" | null;
  notes: string;
  onApply: (text: string) => void;
};

export function AiFeedbackAssistant({ studentName, videoTitle, decision, notes, onApply }: Props) {
  const [suggestion, setSuggestion] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const run = async () => {
    if (!decision) { toast.error("Pick a verification status first."); return; }
    setLoading(true);
    try {
      const res = await generateTeacherFeedback({ studentName, videoTitle, decision, notes });
      setSuggestion(res.text.trim());
    } catch (e) {
      toast.error("Couldn't generate suggestion.");
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(suggestion);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="relative overflow-hidden rounded-xl border-2 border-gold/40 bg-gradient-to-br from-royal-soft via-card to-gold-soft/30 p-4">
      <div className="pointer-events-none absolute -right-6 -top-6 h-24 w-24 rounded-full bg-gold/20 blur-2xl" />
      <div className="relative flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="grid h-8 w-8 place-items-center rounded-lg bg-gradient-to-br from-gold to-saffron text-white shadow-soft">
            <Sparkles className="h-4 w-4" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold text-royal">AI Feedback Assistant</span>
              <span className="rounded-full bg-gold px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-royal">New</span>
            </div>
            <div className="text-[11px] text-muted-foreground">Generate polite, constructive remarks in one click.</div>
          </div>
        </div>
        <Button size="sm" onClick={run} disabled={loading} className="bg-royal hover:bg-royal/90">
          {loading ? <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Writing…</> : <><Sparkles className="mr-1.5 h-3.5 w-3.5" /> {suggestion ? "Regenerate" : "Suggest remarks"}</>}
        </Button>
      </div>

      {suggestion && (
        <div className="relative mt-3 rounded-lg border bg-background/80 p-3 text-sm leading-relaxed">
          {suggestion}
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" onClick={() => onApply(suggestion)} className="bg-gold text-royal hover:bg-gold/90">
              Use this remark
            </Button>
            <Button size="sm" variant="outline" onClick={copy}>
              {copied ? <><Check className="mr-1 h-3.5 w-3.5" /> Copied</> : <><Copy className="mr-1 h-3.5 w-3.5" /> Copy</>}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
